import React, { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import AppHeader from "@/components/AppHeader";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Archive, Users as UsersIcon, MapPin, UserCheck, Trash2, RotateCcw, LayoutGrid, List as ListIcon } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const fmtDate = (s) => s ? new Date(s).toLocaleString("it-IT", { day: "2-digit", month: "short", year: "numeric" }) : "—";

const VacantBoards = () => {
  const { user } = useAuth();
  const [boards, setBoards] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [reassignTo, setReassignTo] = useState(null); // board to reassign
  const [q, setQ] = useState("");
  const [toDelete, setToDelete] = useState(null);
  const [view, setView] = useState("cards"); // "cards" | "list" — admin toggle
  const [selected, setSelected] = useState(new Set()); // bulk selection (list view)
  const [bulkBusy, setBulkBusy] = useState(false);

  const toggleSel = (id) => setSelected((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const toggleAll = () => setSelected((s) => s.size === boards.length ? new Set() : new Set(boards.map((b) => b.id)));

  const bulkDelete = async () => {
    if (selected.size === 0) return;
    if (!window.confirm(`Eliminare ${selected.size} schede vacanti?`)) return;
    setBulkBusy(true);
    try {
      await Promise.all([...selected].map((id) => api.delete(`/boards/${id}`)));
      toast.success(`${selected.size} schede eliminate`);
      setSelected(new Set());
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
    finally { setBulkBusy(false); }
  };

  const load = async () => {
    setLoading(true);
    try {
      const [b, u] = await Promise.all([
        api.get("/admin/vacant-boards"),
        api.get("/users/directory"),
      ]);
      setBoards(b.data);
      setUsers(u.data);
    } catch { toast.error("Errore caricamento"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  if (user && user.role !== "admin") return <Navigate to="/boards" replace />;

  const reassign = async (targetUser) => {
    try {
      await api.put(`/boards/${reassignTo.id}/reassign`, { new_owner_id: targetUser.user_id });
      toast.success(`Assegnata a ${targetUser.name || targetUser.email}`);
      setReassignTo(null); setQ("");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
  };

  const confirmDelete = async () => {
    try {
      await api.delete(`/boards/${toDelete.id}`);
      toast.success("Scheda eliminata");
      setToDelete(null);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
  };

  const candidates = users.filter((u) => !u.blocked && u.user_id !== reassignTo?.owner_id)
    .filter((u) => !q || (u.name || "").toLowerCase().includes(q.toLowerCase()) || (u.email || "").toLowerCase().includes(q.toLowerCase()));

  return (
    <div className="min-h-screen">
      <AppHeader />
      <main className="max-w-6xl mx-auto p-6">
        <div className="mb-6">
          <div className="text-[10px] uppercase tracking-[0.3em] text-slate-500 flex items-center gap-2">
            <Archive className="w-3 h-3" /> Archivio amministratore
          </div>
          <h1 className="font-display font-bold text-4xl tracking-tighter leading-none mt-1">Schede vacanti</h1>
          <p className="text-sm text-slate-500 mt-2 max-w-2xl">
            Schede orfane perché il proprietario è stato bloccato o rimosso. Puoi <b>riassegnarle</b> a un altro utente o <b>eliminarle</b>.
          </p>
          <div className="mt-4 inline-flex rounded-full bg-slate-100 p-1" data-testid="vb-view-toggle">
            <button onClick={() => setView("cards")} className={`rounded-full px-3 py-1 text-xs font-semibold ${view === "cards" ? "bg-white shadow-sm" : "text-slate-500"}`} data-testid="vb-view-cards">
              <LayoutGrid className="w-3.5 h-3.5 inline mr-1" /> Card
            </button>
            <button onClick={() => setView("list")} className={`rounded-full px-3 py-1 text-xs font-semibold ${view === "list" ? "bg-white shadow-sm" : "text-slate-500"}`} data-testid="vb-view-list">
              <ListIcon className="w-3.5 h-3.5 inline mr-1" /> Lista
            </button>
          </div>
        </div>

        {loading ? (
          <div className="text-sm text-slate-500">Caricamento…</div>
        ) : boards.length === 0 ? (
          <div className="ios-card p-12 text-center">
            <div className="w-14 h-14 mx-auto rounded-2xl bg-slate-100 flex items-center justify-center mb-4">
              <Archive className="w-6 h-6 text-slate-500" />
            </div>
            <div className="font-display font-bold text-xl">Nessuna scheda vacante</div>
            <p className="text-sm text-slate-500 mt-2">Le schede orfane compariranno qui quando bloccherai o rimuoverai un utente.</p>
          </div>
        ) : view === "list" ? (
          <div className="ios-card overflow-hidden" data-testid="vb-list">
            {selected.size > 0 && (
              <div className="flex items-center justify-between px-4 py-2.5 bg-slate-900 text-white text-xs" data-testid="vb-bulk-bar">
                <span>{selected.size} scheda{selected.size !== 1 ? "e" : ""} selezionat{selected.size !== 1 ? "e" : "a"}</span>
                <div className="flex gap-2">
                  <button onClick={() => setSelected(new Set())} className="rounded-full bg-white/10 hover:bg-white/20 px-3 py-1" data-testid="vb-bulk-clear">Deseleziona</button>
                  <button onClick={bulkDelete} disabled={bulkBusy} className="rounded-full bg-rose-500 hover:bg-rose-600 px-3 py-1 font-semibold" data-testid="vb-bulk-delete">
                    {bulkBusy ? "Elimino…" : `Elimina ${selected.size}`}
                  </button>
                </div>
              </div>
            )}
            <table className="ios-table w-full">
              <thead>
                <tr>
                  <th className="w-8">
                    <input type="checkbox" checked={boards.length > 0 && selected.size === boards.length} onChange={toggleAll} data-testid="vb-select-all" className="w-4 h-4 accent-[#0A84FF] cursor-pointer" />
                  </th>
                  <th>Scheda</th><th>Motivo</th><th className="num">Immobili</th><th className="num">Membri</th>
                  <th>Proprietario originale</th><th>Vacante dal</th><th className="text-right">Azioni</th>
                </tr>
              </thead>
              <tbody>
                {boards.map((b) => (
                  <tr key={b.id} data-testid={`vacant-row-${b.id}`} className={selected.has(b.id) ? "bg-blue-50" : ""}>
                    <td>
                      <input type="checkbox" checked={selected.has(b.id)} onChange={() => toggleSel(b.id)} data-testid={`vb-select-${b.id}`} className="w-4 h-4 accent-[#0A84FF] cursor-pointer" />
                    </td>
                    <td>
                      <div className="font-semibold truncate max-w-[220px]">{b.name}</div>
                      {b.description && <div className="text-[11px] text-slate-500 truncate max-w-[220px]">{b.description}</div>}
                    </td>
                    <td>
                      <Badge className="rounded-full bg-amber-100 text-amber-800 text-[9px] uppercase tracking-widest border-0">
                        {b.vacant_reason === "user_blocked" ? "Bloccato" : "Rimosso"}
                      </Badge>
                    </td>
                    <td className="num font-mono-num">{b.property_count}</td>
                    <td className="num font-mono-num">{b.members_count}</td>
                    <td className="text-xs text-slate-600 truncate max-w-[200px]">
                      {b.original_owner ? (b.original_owner.name || b.original_owner.email) : "—"}
                    </td>
                    <td className="text-xs text-slate-500">{fmtDate(b.vacant_at)}</td>
                    <td className="text-right whitespace-nowrap">
                      <Button size="sm" className="rounded-full bg-[#007AFF] hover:bg-[#0060df] h-7 px-3 text-[11px]" onClick={() => setReassignTo(b)} data-testid={`reassign-list-${b.id}`}>
                        <UserCheck className="w-3 h-3 mr-1" /> Riassegna
                      </Button>
                      <Button size="sm" variant="ghost" className="rounded-full text-rose-600 hover:bg-rose-50 h-7 w-7 p-0 ml-1" onClick={() => setToDelete(b)} data-testid={`delete-list-${b.id}`}>
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {boards.map((b) => (
              <div key={b.id} className="ios-card p-5" data-testid={`vacant-board-${b.id}`}>
                <div className="flex items-center justify-between mb-3">
                  <div className="w-10 h-10 rounded-2xl bg-slate-200 flex items-center justify-center">
                    <Archive className="w-4 h-4 text-slate-600" />
                  </div>
                  <Badge className="rounded-full bg-amber-100 text-amber-800 text-[9px] uppercase tracking-widest border-0">
                    {b.vacant_reason === "user_blocked" ? "Bloccato" : "Rimosso"}
                  </Badge>
                </div>
                <h3 className="font-display font-bold text-lg leading-tight tracking-tight">{b.name}</h3>
                {b.description && <p className="text-xs text-slate-500 mt-1.5 line-clamp-2">{b.description}</p>}
                <div className="flex items-center gap-4 mt-4 text-xs text-slate-600">
                  <div className="flex items-center gap-1"><MapPin className="w-3.5 h-3.5" /><span className="font-mono-num">{b.property_count}</span></div>
                  <div className="flex items-center gap-1"><UsersIcon className="w-3.5 h-3.5" /><span className="font-mono-num">{b.members_count}</span></div>
                  <div className="text-slate-400">Vacante dal {fmtDate(b.vacant_at)}</div>
                </div>
                {b.original_owner && (
                  <div className="mt-3 pt-3 border-t border-slate-100 text-[11px] text-slate-500">
                    Prop. originale: <span className="text-slate-700">{b.original_owner.name || b.original_owner.email}</span>
                  </div>
                )}
                <div className="flex items-center gap-2 mt-4">
                  <Button size="sm" className="flex-1 rounded-full bg-[#007AFF] hover:bg-[#0060df]" onClick={() => setReassignTo(b)} data-testid={`reassign-${b.id}`}>
                    <UserCheck className="w-3.5 h-3.5 mr-1.5" /> Riassegna
                  </Button>
                  <Button size="sm" variant="ghost" className="rounded-full text-rose-600 hover:bg-rose-50" onClick={() => setToDelete(b)} data-testid={`delete-vacant-${b.id}`}>
                    <Trash2 className="w-3.5 h-3.5" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </main>

      <Dialog open={!!reassignTo} onOpenChange={(v) => !v && setReassignTo(null)}>
        <DialogContent className="max-w-lg rounded-2xl bg-white">
          <DialogHeader>
            <DialogTitle className="font-display font-bold text-2xl tracking-tight">Riassegna scheda</DialogTitle>
          </DialogHeader>
          <div className="text-sm text-slate-600">Scegli il nuovo proprietario di <b>"{reassignTo?.name}"</b>.</div>
          <Input
            data-testid="reassign-search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Cerca utente…"
            className="rounded-xl h-10 bg-slate-100 border-0"
          />
          <div className="space-y-1.5 max-h-[300px] overflow-y-auto">
            {candidates.slice(0, 30).map((u) => (
              <button key={u.user_id} onClick={() => reassign(u)} className="w-full flex items-center gap-3 p-2.5 rounded-xl hover:bg-slate-50 text-left" data-testid={`reassign-pick-${u.user_id}`}>
                {u.picture ? (
                  <img src={u.picture} alt="" className="w-9 h-9 rounded-full" />
                ) : (
                  <div className="w-9 h-9 rounded-full bg-slate-200 text-slate-700 flex items-center justify-center font-semibold text-sm">
                    {(u.name || u.email)[0]?.toUpperCase()}
                  </div>
                )}
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-medium truncate">{u.name || u.email}</div>
                  <div className="text-[11px] text-slate-500 truncate">{u.email}</div>
                </div>
                <Badge variant="outline" className="rounded-full text-[9px] uppercase">{u.role}</Badge>
              </button>
            ))}
            {candidates.length === 0 && <div className="text-xs text-slate-400 italic text-center py-4">Nessun utente disponibile</div>}
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-full" onClick={() => setReassignTo(null)}>Annulla</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!toDelete} onOpenChange={(v) => !v && setToDelete(null)}>
        <AlertDialogContent className="rounded-2xl">
          <AlertDialogHeader>
            <AlertDialogTitle>Eliminare "{toDelete?.name}"?</AlertDialogTitle>
            <AlertDialogDescription>
              Verranno eliminati anche i {toDelete?.property_count || 0} immobili contenuti. Azione irreversibile.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-full">Annulla</AlertDialogCancel>
            <AlertDialogAction className="rounded-full bg-rose-600 hover:bg-rose-700" onClick={confirmDelete} data-testid="confirm-delete-vacant">Elimina</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default VacantBoards;
